import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import html2canvas from 'html2canvas';

interface SelectionRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface UseScreenshotSelectionOptions {
    onCapture: (file: File) => void;
    onError: (message: string) => void;
}

export interface ScreenshotSelectionController {
    selectingScreenshot: boolean;
    selectionRect: SelectionRect | null;
    startSelection: () => void;
    cancelSelection: () => void;
    captureSelection: () => Promise<void>;
    handleMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
}

const MIN_SELECTION_SIZE = 8;

function waitForNextFrame(): Promise<void> {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

export function useScreenshotSelection({ onCapture, onError }: UseScreenshotSelectionOptions): ScreenshotSelectionController {
    const [selectingScreenshot, setSelectingScreenshot] = useState(false);
    const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
    const dragStartRef = useRef<{ x: number; y: number } | null>(null);

    const cancelSelection = useCallback(() => {
        dragStartRef.current = null;
        setSelectionRect(null);
        setSelectingScreenshot(false);
    }, []);

    const startSelection = useCallback(() => {
        dragStartRef.current = null;
        setSelectionRect(null);
        setSelectingScreenshot(true);
    }, []);

    const captureSelection = useCallback(async () => {
        if (!selectionRect) {
            onError('Select an area first.');
            return;
        }

        const appRoot = document.getElementById('root');
        if (!appRoot) {
            onError('Unable to capture the page.');
            return;
        }

        const rootRect = appRoot.getBoundingClientRect();
        const cropLeft = Math.max(0, selectionRect.x - rootRect.left);
        const cropTop = Math.max(0, selectionRect.y - rootRect.top);
        const cropRight = Math.min(rootRect.right, selectionRect.x + selectionRect.width);
        const cropBottom = Math.min(rootRect.bottom, selectionRect.y + selectionRect.height);
        const cropWidth = cropRight - (rootRect.left + cropLeft);
        const cropHeight = cropBottom - (rootRect.top + cropTop);

        if (cropWidth < MIN_SELECTION_SIZE || cropHeight < MIN_SELECTION_SIZE) {
            onError('Selected area is too small.');
            return;
        }

        setSelectingScreenshot(false);
        await waitForNextFrame();
        await waitForNextFrame();

        try {
            const capturedCanvas = await html2canvas(appRoot, {
                backgroundColor: null,
                useCORS: true,
                logging: false,
                scale: window.devicePixelRatio || 1,
            });

            const scaleX = capturedCanvas.width / rootRect.width;
            const scaleY = capturedCanvas.height / rootRect.height;
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(cropWidth * scaleX);
            canvas.height = Math.round(cropHeight * scaleY);

            const context = canvas.getContext('2d');
            if (!context) throw new Error('Unable to create screenshot canvas.');

            context.drawImage(
                capturedCanvas,
                Math.round(cropLeft * scaleX),
                Math.round(cropTop * scaleY),
                Math.round(cropWidth * scaleX),
                Math.round(cropHeight * scaleY),
                0,
                0,
                canvas.width,
                canvas.height
            );

            const blob = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(resolve, 'image/png');
            });

            if (!blob) throw new Error('Unable to create screenshot file.');

            onCapture(
                new File([blob], `screen-capture-${Date.now()}.png`, {
                    type: 'image/png',
                })
            );
        } catch (error) {
            console.error(error);
            onError('Failed to capture the selected area.');
        } finally {
            dragStartRef.current = null;
            setSelectionRect(null);
        }
    }, [onCapture, onError, selectionRect]);

    const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();
        dragStartRef.current = { x: event.clientX, y: event.clientY };
        setSelectionRect({
            x: event.clientX,
            y: event.clientY,
            width: 0,
            height: 0,
        });
    }, []);

    const updateSelectionRect = useCallback((clientX: number, clientY: number) => {
        const dragStart = dragStartRef.current;
        if (!dragStart) return;

        const left = Math.min(dragStart.x, clientX);
        const top = Math.min(dragStart.y, clientY);
        const width = Math.abs(clientX - dragStart.x);
        const height = Math.abs(clientY - dragStart.y);

        setSelectionRect({
            x: left,
            y: top,
            width,
            height,
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        dragStartRef.current = null;
    }, []);

    useEffect(() => {
        if (!selectingScreenshot) return undefined;

        const previousBodyUserSelect = document.body.style.userSelect;
        const previousBodyWebkitUserSelect = document.body.style.webkitUserSelect;
        const captureClassName = 'report-issue-capture-active';
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        document.body.classList.add(captureClassName);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') cancelSelection();
        };
        const preventBrowserSelection = (event: Event) => {
            event.preventDefault();
        };
        const handleWindowMouseMove = (event: globalThis.MouseEvent) => {
            if (!dragStartRef.current) return;
            event.preventDefault();
            updateSelectionRect(event.clientX, event.clientY);
        };
        const handleWindowMouseUp = () => {
            handleMouseUp();
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('selectstart', preventBrowserSelection);
        document.addEventListener('dragstart', preventBrowserSelection);
        window.addEventListener('mousemove', handleWindowMouseMove, true);
        window.addEventListener('mouseup', handleWindowMouseUp, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('selectstart', preventBrowserSelection);
            document.removeEventListener('dragstart', preventBrowserSelection);
            window.removeEventListener('mousemove', handleWindowMouseMove, true);
            window.removeEventListener('mouseup', handleWindowMouseUp, true);
            document.body.style.userSelect = previousBodyUserSelect;
            document.body.style.webkitUserSelect = previousBodyWebkitUserSelect;
            document.body.classList.remove(captureClassName);
        };
    }, [cancelSelection, handleMouseUp, selectingScreenshot, updateSelectionRect]);

    return {
        selectingScreenshot,
        selectionRect,
        startSelection,
        cancelSelection,
        captureSelection,
        handleMouseDown,
    };
}
